import { getDatabase, ref as dbRef, set, push, update } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import { PhotoModel } from '../types/protocols';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { PreparedDrawing } from '../utils/pdf/blueprintSource';
import { blueprintSourceStoragePath } from '../utils/pdf/blueprintSourcePath';
import { BLUEPRINT_SOURCE_CONTENT_TYPE, buildBlueprintSource } from '../utils/pdf/blueprintSourceLogic';
import { recordActivity } from './activityApi';

/** Where a drawing lives: the drawings map (DB path == Storage path) and the pointer that makes it current. */
export interface DrawingUploadTarget {
    blueprintsPath: string;
    currentBlueprintKeyPath: string;
    /** Root-relative DB paths written in the same multi-path update as the pointer. */
    extraPointerUpdates?: Record<string, unknown>;
}

/** Result of an upload with `deferPointer`: the drawing node exists, nothing points at it yet. */
export interface DeferredBlueprintUpload {
    key: string;
    blueprint: PhotoModel;
    /** DB path of the written drawing node (== the raster's Storage path). */
    blueprintPath: string;
    /** Root-relative writes that would have made the drawing current (pointer + any target extras). */
    pointerUpdates: Record<string, unknown>;
}

/**
 * Writes a prepared drawing: raster object, then the PDF source object at
 * `<raster path>_source`, then the node (with `source`), then the pointer —
 * unless `deferPointer`, in which case the pointer writes are returned for
 * the caller to commit later (marker relocation). Order matters for the
 * upcoming rules (the node is written last and complete). No rollback on a
 * partial failure: an orphaned object costs storage only (plan D15).
 */
export const uploadDrawing = async (
    prepared: PreparedDrawing,
    target: DrawingUploadTarget,
    authorUid: string,
    deferPointer: boolean
): Promise<DeferredBlueprintUpload> => {
    const db = getDatabase();
    const storage = getStorage();

    const newBlueprintRef = push(dbRef(db, target.blueprintsPath));
    const key = newBlueprintRef.key;
    if (!key) {
        throw new Error('Kunde inte skapa nyckel för ritningen');
    }

    const storagePath = `${target.blueprintsPath}/${key}`;
    const sourcePath = blueprintSourceStoragePath(storagePath);

    await uploadBytes(storageRef(storage, storagePath), prepared.raster.blob, {
        contentType: prepared.raster.mime,
    });
    await uploadBytes(storageRef(storage, sourcePath), prepared.source, {
        contentType: BLUEPRINT_SOURCE_CONTENT_TYPE,
    });

    const date = formatDateForStorage(new Date());
    const blueprint: PhotoModel = {
        key,
        author: authorUid,
        date,
        width: prepared.raster.width,
        height: prepared.raster.height,
        storagePath,
        fileSize: prepared.raster.blob.size,
        source: buildBlueprintSource({
            storagePath: sourcePath,
            fileSize: prepared.source.byteLength,
            origin: prepared.origin,
            sourcePage: prepared.sourcePage,
            sourceFileName: prepared.sourceFileName,
            author: authorUid,
            date,
        }),
    };
    await set(newBlueprintRef, blueprint);

    const pointerUpdates: Record<string, unknown> = {
        ...(target.extraPointerUpdates || {}),
        [target.currentBlueprintKeyPath]: key,
    };
    if (!deferPointer) {
        await update(dbRef(db), pointerUpdates);
    }
    recordActivity('drawing.uploaded', { buildingKey: /^buildings\/([^/]+)\//.exec(target.blueprintsPath)?.[1], targetKey: key });
    return { key, blueprint, blueprintPath: storagePath, pointerUpdates };
};
